import React from 'react';
import { Box } from '@mui/material';

export type DeviceBrand = 'shelly' | 'sonos' | 'samsung' | 'androidtv' | 'lg' | 'evohome' | 'pileta';

/** Mismo diámetro que DeviceTypeImage (círculo del ícono). */
const ICON_CIRCLE_PX = 60;
/** Chip siempre dentro del círculo (margen ~3px por lado). */
const CHIP_MAX_PX = ICON_CIRCLE_PX - 6;

const BRAND_LABEL: Record<DeviceBrand, string> = {
  shelly: 'Shelly',
  sonos: 'Sonos',
  samsung: 'Samsung',
  androidtv: 'Android',
  lg: 'LG',
  evohome: 'EvoHome',
  pileta: 'Pileta',
};

/** Color del texto según marca (fondo siempre negro). */
const BRAND_COLOR: Record<DeviceBrand, string> = {
  shelly: '#ff4444',
  sonos: '#ffffff',
  samsung: '#2391FF',
  androidtv: '#81C784',
  lg: '#A50034',
  evohome: '#FF8A65',
  pileta: '#4FC3F7',
};

/** Texto más largo → tipografía más chica para no salirse del círculo. */
function fontSizeForLabel(label: string): string {
  const len = label.length;
  if (len >= 8) return '0.42rem';
  if (len >= 7) return '0.46rem'; // Samsung
  if (len >= 6) return '0.52rem';
  return '0.58rem';
}

interface DeviceBrandChipProps {
  brand: DeviceBrand;
  /** Solo visible en modo edición Settings. */
  visible?: boolean;
}

/**
 * Chip centrado sobre el círculo del ícono de la tarjeta (modo edición).
 * Fondo negro + texto de marca con color por brand.
 * Nunca excede el diámetro del círculo; nombres largos usan fuente menor.
 */
const DeviceBrandChip: React.FC<DeviceBrandChipProps> = ({ brand, visible = true }) => {
  if (!visible) return null;

  const label = BRAND_LABEL[brand];

  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 12,
        pointerEvents: 'none',
        userSelect: 'none',
        boxSizing: 'border-box',
        maxWidth: CHIP_MAX_PX,
        width: 'max-content',
        backgroundColor: '#000000',
        color: BRAND_COLOR[brand],
        fontSize: fontSizeForLabel(label),
        fontWeight: 800,
        letterSpacing: '0.01em',
        lineHeight: 1.15,
        px: 0.85,
        py: 0.4,
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.55)',
      }}
    >
      {label}
    </Box>
  );
};

/** Wrapper del ícono circular: posiciona el chip de marca encima en edición. */
export const DeviceIconWithBrandChip: React.FC<{
  brand: DeviceBrand;
  showBrandChip?: boolean;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}> = ({ brand, showBrandChip = false, children, sx }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'inline-flex',
      flexShrink: 0,
      ...sx,
    }}
  >
    {children}
    <DeviceBrandChip brand={brand} visible={showBrandChip} />
  </Box>
);

export default DeviceBrandChip;
